import { serializePageSize } from 'Models/pagination';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import { isTruthy } from 'Utils/guards';
import { addPlatformRoute } from 'Utils/url';

import { uriEncode, uriEncodeTerm } from './uriEncoding';

export type RequestShape = Pick<
  SearchRequestDefaults,
  | 'catalog'
  | 'defaultSearch'
  | 'filterQuery'
  | 'pageNumber'
  | 'pageSize'
  | 'pageSizeMode'
  | 'query'
  | 'selection'
  | 'sort'
>;

type GetUriOptions = {
  pathname?: string;
  goToSearchPage?: boolean;
  stayOnPage?: boolean;
};

export default function getUriFromRequest(
  request: RequestShape,
  { pathname = '', goToSearchPage = false, stayOnPage = false }: GetUriOptions = {},
): URL {
  const url = new URL(
    pathname || getPathname(goToSearchPage || (!stayOnPage && !requestConfig.hasSearchResults)) || '',
    getBaseUrl(),
  );
  if (url.origin === window.location.origin) {
    url.pathname = addPlatformRoute(url.pathname);
  }
  url.search = [url.search, getSearch(request)].filter(isTruthy).join('&');
  url.hash = getHash(request);
  return url;
}

function getBaseUrl(): string {
  return (
    [uiConfig.searchPageBaseUrl, document.baseURI, window.location.origin].find((baseUrl) =>
      isValidBaseUrl(baseUrl),
    ) || window.location.href
  );
}

function isValidBaseUrl(baseUrl: string | undefined): boolean {
  if (!baseUrl) {
    return false;
  }

  try {
    new URL('/', baseUrl);
    return true;
  } catch {
    return false;
  }
}

function getPathname(goToSearchPage: boolean) {
  return goToSearchPage ? uiConfig.searchPageUrl : window.location.pathname;
}

function getSearch(request: RequestShape) {
  if (uiConfig.useLocationHash) {
    return request.defaultSearch || '';
  }
  const facetsUri = makeFacetsUri(request);
  const result = [
    request.defaultSearch,
    request.defaultSearch && facetsUri && 'Facet=',
    facetsUri,
    makeParamsUri(request),
  ]
    .filter(isTruthy)
    .join('&')
    .replace('Facet=&', 'Facet=');

  return uiConfig.platform === 'shift4shop' ? result.replace(/\*$/, '%2A') : result;
}

function getHash(request: RequestShape) {
  return uiConfig.useLocationHash
    ? [makeFacetsUri(request), makeParamsUri(request)].filter(isTruthy).join('&')
    : '';
}

export function makeFacetsUri({ selection = [] }: RequestShape): string {
  return selection
    .filter((sel) => !requestConfig.clientSideStoredFields.includes(sel.field))
    .map(({ field, term }) => `${uriEncode(field)}/${uriEncodeTerm(term)}`)
    .join('/');
}

const paramNames = ['query', 'filterQuery', 'catalog', 'pageSize', 'pageNumber', 'sort'] as const;

type ParamName = (typeof paramNames)[number];

type ProcessedParamValue = string | number | false | undefined;

const processors: { [K in ParamName]: (value: RequestShape[K]) => ProcessedParamValue } = {
  query: (v) => v && uriEncode(v),
  filterQuery: (v) => v && uriEncode(v),
  catalog: (v) => v && uriEncode(v),
  pageSize: (v) => v !== requestConfig.defaultPageSize && v,
  pageNumber: (v) => v && +v + 1,
  sort: (v) => v !== requestConfig.defaultSort && v,
};

const paramMapping: Partial<Record<ParamName, string>> = {
  pageNumber: requestConfig.pageUriParam,
};

function getProcessedParamValue<K extends ParamName>(request: RequestShape, name: K) {
  if (name === 'pageSize') {
    const pageSize = serializePageSize(request.pageSize, request.pageSizeMode);
    return processors.pageSize(pageSize);
  }
  return processors[name](request[name]);
}

function makeParamsUri(request: RequestShape) {
  return paramNames
    .reduce((params: string[], name) => {
      const value = getProcessedParamValue(request, name);
      if (value) {
        const paramName = paramMapping[name] ?? name;
        params.push(`${paramName}=${value}`);
      }
      return params;
    }, [])
    .join('&');
}
